import { useCallback, useRef, useState } from "react";

type UseFileDropOptions = {
    onFilesDropped: (files: File[]) => void;
    disabled?: boolean;
};

type UseFileDropResult = {
    isDragging: boolean;
    handleDragOver: React.DragEventHandler<HTMLDivElement>;
    handleDragEnter: React.DragEventHandler<HTMLDivElement>;
    handleDragLeave: React.DragEventHandler<HTMLDivElement>;
    handleDrop: React.DragEventHandler<HTMLDivElement>;
};

export const useFileDrop = (options: UseFileDropOptions): UseFileDropResult => {
    const { onFilesDropped, disabled = false } = options;

    const [isDragging, setIsDragging] = useState(false);
    const dragCounterRef = useRef(0);

    const addDroppedFiles = useCallback(
        (files: FileList | File[]) => {
            const filesArray = Array.from(files);

            if (!filesArray.length) return;
            if (disabled) return;

            onFilesDropped(filesArray);
        },
        [onFilesDropped, disabled]
    );

    const handleDragOver: React.DragEventHandler<HTMLDivElement> = useCallback((event) => {
        if (disabled) return;

        event.preventDefault();
        event.stopPropagation();
    }, [disabled]);

    const handleDragEnter: React.DragEventHandler<HTMLDivElement> = useCallback((event) => {
        if (disabled) return;

        event.preventDefault();
        event.stopPropagation();

        dragCounterRef.current += 1;

        if (event.dataTransfer?.items && event.dataTransfer.items.length > 0) {
            setIsDragging(true);
        }
    }, [disabled]);

    const handleDragLeave: React.DragEventHandler<HTMLDivElement> = useCallback((event) => {
        if (disabled) return;

        event.preventDefault();
        event.stopPropagation();

        dragCounterRef.current -= 1;

        if (dragCounterRef.current === 0) {
            setIsDragging(false);
        }
    }, [disabled]);

    const handleDrop: React.DragEventHandler<HTMLDivElement> = useCallback((event) => {
        if (disabled) return;

        event.preventDefault();
        event.stopPropagation();

        setIsDragging(false);
        dragCounterRef.current = 0;

        if (event.dataTransfer?.files && event.dataTransfer.files.length > 0) {
            addDroppedFiles(event.dataTransfer.files);
            event.dataTransfer.clearData();
        }
    }, [addDroppedFiles, disabled]);

    return {
        isDragging,
        handleDragOver,
        handleDragEnter,
        handleDragLeave,
        handleDrop,
    };
};

